/**
 * @engine/physics2d - Narrow-Phase Collision Detection
 *
 * SAT-based collision detection for all primitive pairs.
 * Returns contact manifolds with points, normals, and penetration.
 *
 * Dispatch table:
 *   Circle  × Circle  → circleVsCircle
 *   Circle  × Box     → circleVsBox
 *   Circle  × Capsule → circleVsCapsule
 *   Circle  × Polygon → circleVsPolygon
 *   Box     × Box     → polygonVsPolygon (boxes are 4-vert polygons)
 *   Box     × Polygon → polygonVsPolygon
 *   Capsule × *       → decomposed to circle sweeps
 *   Polygon × Polygon → SAT with clipping
 */

import { ColliderType, MAX_CONTACT_POINTS } from '../types';
import type { PhysVec2, ContactPoint2D, CollisionManifold } from '../types';
import type { ColliderState } from '../types';

// ============================================================================
// TRANSFORM HELPERS
// ============================================================================

interface Transform2D {
  x: number;
  y: number;
  cos: number;
  sin: number;
}

function localToWorld(t: Transform2D, localX: number, localY: number): PhysVec2 {
  return {
    x: t.x + localX * t.cos - localY * t.sin,
    y: t.y + localX * t.sin + localY * t.cos,
  };
}

function worldToLocal(t: Transform2D, worldX: number, worldY: number): PhysVec2 {
  const dx = worldX - t.x;
  const dy = worldY - t.y;
  return {
    x: dx * t.cos + dy * t.sin,
    y: -dx * t.sin + dy * t.cos,
  };
}

function makeTransform(posX: number, posY: number, rot: number, offsetX: number, offsetY: number): Transform2D {
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  return {
    x: posX + offsetX * cos - offsetY * sin,
    y: posY + offsetX * sin + offsetY * cos,
    cos, sin,
  };
}

// ============================================================================
// NARROWPHASE DISPATCH
// ============================================================================

/**
 * Test two colliders for overlap and generate contact manifold.
 * Returns null if no collision.
 */
export function testCollision(
  a: ColliderState,
  b: ColliderState,
  posA: PhysVec2,
  rotA: number,
  posB: PhysVec2,
  rotB: number,
): CollisionManifold | null {
  const tA = makeTransform(posA.x, posA.y, rotA + a.rotation, a.offset.x, a.offset.y);
  const tB = makeTransform(posB.x, posB.y, rotB + b.rotation, b.offset.x, b.offset.y);

  // Dispatch based on shape types
  const typeA = a.type;
  const typeB = b.type;

  // Ensure consistent pair ordering for dispatch
  if (typeA > typeB) {
    const result = testCollisionSorted(b, a, tB, tA);
    if (result) {
      // Flip normal and swap body indices
      result.normal.x = -result.normal.x;
      result.normal.y = -result.normal.y;
      const tmp = result.bodyIndexA;
      result.bodyIndexA = result.bodyIndexB;
      result.bodyIndexB = tmp;
      const tmpC = result.colliderA;
      result.colliderA = result.colliderB;
      result.colliderB = tmpC;
    }
    return result;
  }

  return testCollisionSorted(a, b, tA, tB);
}

function testCollisionSorted(
  a: ColliderState,
  b: ColliderState,
  tA: Transform2D,
  tB: Transform2D,
): CollisionManifold | null {
  const typeA = a.type;
  const typeB = b.type;

  // Circle vs *
  if (typeA === ColliderType.Circle) {
    if (typeB === ColliderType.Circle) return circleVsCircle(a, b, tA, tB);
    if (typeB === ColliderType.Box) return circleVsBox(a, b, tA, tB);
    if (typeB === ColliderType.Capsule) return circleVsCapsule(a, b, tA, tB);
    if (typeB === ColliderType.Polygon) return circleVsPolygon(a, b, tA, tB);
  }

  // Box vs *
  if (typeA === ColliderType.Box) {
    if (typeB === ColliderType.Circle) return circleVsBox(b, a, tB, tA);
    if (typeB === ColliderType.Box) return boxVsBox(a, b, tA, tB);
    if (typeB === ColliderType.Capsule) return boxVsCapsule(a, b, tA, tB);
    if (typeB === ColliderType.Polygon) return boxVsPolygon(a, b, tA, tB);
  }

  // Capsule vs *
  if (typeA === ColliderType.Capsule) {
    if (typeB === ColliderType.Capsule) return capsuleVsCapsule(a, b, tA, tB);
    if (typeB === ColliderType.Polygon) return capsuleVsPolygon(a, b, tA, tB);
  }

  // Polygon vs Polygon
  if (typeA === ColliderType.Polygon && typeB === ColliderType.Polygon) {
    return polygonVsPolygon(a, b, tA, tB);
  }

  return null;
}

// ============================================================================
// CIRCLE vs CIRCLE
// ============================================================================

function circleVsCircle(
  a: ColliderState, b: ColliderState,
  tA: Transform2D, tB: Transform2D,
): CollisionManifold | null {
  const dx = tB.x - tA.x;
  const dy = tB.y - tA.y;
  const distSq = dx * dx + dy * dy;
  const sumR = a.radius + b.radius;

  if (distSq >= sumR * sumR) return null;

  const dist = Math.sqrt(distSq);
  let nx: number, ny: number;

  if (dist > 1e-10) {
    nx = dx / dist;
    ny = dy / dist;
  } else {
    nx = 0;
    ny = 1;
  }

  const separation = dist - sumR;
  const contactX = tA.x + nx * a.radius;
  const contactY = tA.y + ny * a.radius;

  return createManifold(a, b, { x: nx, y: ny }, [
    createContact({ x: contactX, y: contactY }, { x: nx, y: ny }, separation),
  ]);
}

// ============================================================================
// CIRCLE vs BOX
// ============================================================================

function circleVsBox(
  circle: ColliderState, box: ColliderState,
  tCircle: Transform2D, tBox: Transform2D,
): CollisionManifold | null {
  // Transform circle center into box local space
  const local = worldToLocal(tBox, tCircle.x, tCircle.y);
  const hx = box.halfExtents.x;
  const hy = box.halfExtents.y;

  // Clamp circle center to box
  const clampedX = Math.max(-hx, Math.min(hx, local.x));
  const clampedY = Math.max(-hy, Math.min(hy, local.y));

  const dx = local.x - clampedX;
  const dy = local.y - clampedY;
  const distSq = dx * dx + dy * dy;
  const r = circle.radius;

  if (distSq > r * r) return null;

  let nx: number, ny: number, penetration: number;

  if (distSq < 1e-10) {
    // Circle center is inside box — find minimum separation axis
    const distX = hx - Math.abs(local.x);
    const distY = hy - Math.abs(local.y);

    if (distX < distY) {
      nx = local.x >= 0 ? 1 : -1;
      ny = 0;
      penetration = -(distX + r);
    } else {
      nx = 0;
      ny = local.y >= 0 ? 1 : -1;
      penetration = -(distY + r);
    }
  } else {
    const dist = Math.sqrt(distSq);
    nx = dx / dist;
    ny = dy / dist;
    penetration = dist - r;
  }

  // Negate so normal points A(circle)→B(box) instead of B→A
  nx = -nx;
  ny = -ny;

  // Transform normal and contact to world space
  const worldNormal = {
    x: nx * tBox.cos - ny * tBox.sin,
    y: nx * tBox.sin + ny * tBox.cos,
  };
  const worldContact = localToWorld(tBox, clampedX, clampedY);

  return createManifold(circle, box, worldNormal, [
    createContact(worldContact, worldNormal, penetration),
  ]);
}

// ============================================================================
// CIRCLE vs CAPSULE
// ============================================================================

function circleVsCapsule(
  circle: ColliderState, capsule: ColliderState,
  tCircle: Transform2D, tCapsule: Transform2D,
): CollisionManifold | null {
  // Capsule as line segment + radius
  const hs = (capsule.capsuleHeight - capsule.radius * 2) * 0.5;
  let p1: PhysVec2, p2: PhysVec2;

  if (capsule.capsuleDirection === 0) { // Vertical
    p1 = localToWorld(tCapsule, 0, -hs);
    p2 = localToWorld(tCapsule, 0, hs);
  } else { // Horizontal
    p1 = localToWorld(tCapsule, -hs, 0);
    p2 = localToWorld(tCapsule, hs, 0);
  }

  // Closest point on segment to circle center
  const closest = closestPointOnSegment(tCircle.x, tCircle.y, p1, p2);
  const dx = tCircle.x - closest.x;
  const dy = tCircle.y - closest.y;
  const distSq = dx * dx + dy * dy;
  const sumR = circle.radius + capsule.radius;

  if (distSq >= sumR * sumR) return null;

  const dist = Math.sqrt(distSq);
  // Direction from capsule segment toward circle (B→A)
  const dirX = dist > 1e-10 ? dx / dist : 0;
  const dirY = dist > 1e-10 ? dy / dist : 1;
  const separation = dist - sumR;
  const contactX = closest.x + dirX * capsule.radius;
  const contactY = closest.y + dirY * capsule.radius;

  // Negate for A(circle)→B(capsule) convention
  const nx = -dirX;
  const ny = -dirY;

  return createManifold(circle, capsule, { x: nx, y: ny }, [
    createContact({ x: contactX, y: contactY }, { x: nx, y: ny }, separation),
  ]);
}

// ============================================================================
// CIRCLE vs POLYGON (SAT)
// ============================================================================

function circleVsPolygon(
  circle: ColliderState, poly: ColliderState,
  tCircle: Transform2D, tPoly: Transform2D,
): CollisionManifold | null {
  // Transform circle center into polygon local space
  const local = worldToLocal(tPoly, tCircle.x, tCircle.y);
  const r = circle.radius;
  const verts = poly.vertices;
  const normals = poly.normals;
  const n = poly.vertexCount;

  // Find edge with max separation
  let maxSep = -Infinity;
  let bestEdge = 0;

  for (let i = 0; i < n; i++) {
    const sep = normals[i].x * (local.x - verts[i].x) + normals[i].y * (local.y - verts[i].y);
    if (sep > r) return null; // separating axis found
    if (sep > maxSep) {
      maxSep = sep;
      bestEdge = i;
    }
  }

  // Determine contact region
  const v1 = verts[bestEdge];
  const v2 = verts[(bestEdge + 1) % n];

  // Check Voronoi regions
  const d1x = local.x - v1.x;
  const d1y = local.y - v1.y;
  const edgeX = v2.x - v1.x;
  const edgeY = v2.y - v1.y;
  const dot1 = d1x * edgeX + d1y * edgeY;

  if (dot1 <= 0) {
    // Vertex v1 region
    const distSq = d1x * d1x + d1y * d1y;
    if (distSq > r * r) return null;
    const dist = Math.sqrt(distSq);
    // Negate for A(circle)→B(poly) convention
    const nx = dist > 1e-10 ? -d1x / dist : 0;
    const ny = dist > 1e-10 ? -d1y / dist : -1;
    const worldNormal = { x: nx * tPoly.cos - ny * tPoly.sin, y: nx * tPoly.sin + ny * tPoly.cos };
    const worldContact = localToWorld(tPoly, v1.x, v1.y);
    return createManifold(circle, poly, worldNormal, [
      createContact(worldContact, worldNormal, dist - r),
    ]);
  }

  const edgeLenSq = edgeX * edgeX + edgeY * edgeY;
  const d2x = local.x - v2.x;
  const d2y = local.y - v2.y;
  const dot2 = d2x * (-edgeX) + d2y * (-edgeY);

  if (dot2 <= 0) {
    // Vertex v2 region
    const distSq = d2x * d2x + d2y * d2y;
    if (distSq > r * r) return null;
    const dist = Math.sqrt(distSq);
    // Negate for A(circle)→B(poly) convention
    const nx = dist > 1e-10 ? -d2x / dist : 0;
    const ny = dist > 1e-10 ? -d2y / dist : -1;
    const worldNormal = { x: nx * tPoly.cos - ny * tPoly.sin, y: nx * tPoly.sin + ny * tPoly.cos };
    const worldContact = localToWorld(tPoly, v2.x, v2.y);
    return createManifold(circle, poly, worldNormal, [
      createContact(worldContact, worldNormal, dist - r),
    ]);
  }

  // Edge face region — negate outward polygon normal for A(circle)→B(poly)
  const normal = normals[bestEdge];
  const worldNormal = {
    x: -(normal.x * tPoly.cos - normal.y * tPoly.sin),
    y: -(normal.x * tPoly.sin + normal.y * tPoly.cos),
  };
  const t = dot1 / edgeLenSq;
  const closestX = v1.x + t * edgeX;
  const closestY = v1.y + t * edgeY;
  const worldContact = localToWorld(tPoly, closestX, closestY);
  return createManifold(circle, poly, worldNormal, [
    createContact(worldContact, worldNormal, maxSep - r),
  ]);
}

// ============================================================================
// BOX vs BOX (SAT, optimized)
// ============================================================================

function boxVsBox(
  a: ColliderState, b: ColliderState,
  tA: Transform2D, tB: Transform2D,
): CollisionManifold | null {
  return satConvexVsConvex(
    getBoxVerticesWorld(a, tA),
    getBoxNormalsWorld(tA),
    getBoxVerticesWorld(b, tB),
    getBoxNormalsWorld(tB),
    a, b,
  );
}

// ============================================================================
// BOX vs CAPSULE
// ============================================================================

function boxVsCapsule(
  box: ColliderState, capsule: ColliderState,
  tBox: Transform2D, tCapsule: Transform2D,
): CollisionManifold | null {
  // Approximate capsule as 2 circles + check each
  const hs = (capsule.capsuleHeight - capsule.radius * 2) * 0.5;
  const capsuleRadius = capsule.radius;

  let p1: PhysVec2, p2: PhysVec2;
  if (capsule.capsuleDirection === 0) {
    p1 = localToWorld(tCapsule, 0, -hs);
    p2 = localToWorld(tCapsule, 0, hs);
  } else {
    p1 = localToWorld(tCapsule, -hs, 0);
    p2 = localToWorld(tCapsule, hs, 0);
  }

  // Find closest point on capsule segment to box center
  const closest = closestPointOnSegment(tBox.x, tBox.y, p1, p2);

  // Create virtual circle collider at closest point
  const virtualCircle: ColliderState = {
    ...capsule,
    type: ColliderType.Circle,
    radius: capsuleRadius,
    offset: { x: 0, y: 0 },
    rotation: 0,
  };
  const virtualTransform: Transform2D = { x: closest.x, y: closest.y, cos: 1, sin: 0 };

  const result = circleVsBox(virtualCircle, box, virtualTransform, tBox);
  if (result) {
    // Fix manifold to reference original capsule (body/collider indices
    // are already correct from the spread, but be explicit)
    result.colliderA = capsule.colliderIndex;
    result.colliderB = box.colliderIndex;
    result.bodyIndexA = capsule.bodyIndex;
    result.bodyIndexB = box.bodyIndex;
    // Normal already points A(circle/capsule)→B(box) after circleVsBox fix
  }
  return result;
}

// ============================================================================
// BOX vs POLYGON
// ============================================================================

function boxVsPolygon(
  box: ColliderState, poly: ColliderState,
  tBox: Transform2D, tPoly: Transform2D,
): CollisionManifold | null {
  return satConvexVsConvex(
    getBoxVerticesWorld(box, tBox),
    getBoxNormalsWorld(tBox),
    getPolygonVerticesWorld(poly, tPoly),
    getPolygonNormalsWorld(poly, tPoly),
    box, poly,
  );
}

// ============================================================================
// CAPSULE vs CAPSULE
// ============================================================================

function capsuleVsCapsule(
  a: ColliderState, b: ColliderState,
  tA: Transform2D, tB: Transform2D,
): CollisionManifold | null {
  // Segment-segment distance + sum of radii
  const hsA = (a.capsuleHeight - a.radius * 2) * 0.5;
  const hsB = (b.capsuleHeight - b.radius * 2) * 0.5;

  let a1: PhysVec2, a2: PhysVec2, b1: PhysVec2, b2: PhysVec2;
  if (a.capsuleDirection === 0) {
    a1 = localToWorld(tA, 0, -hsA);
    a2 = localToWorld(tA, 0, hsA);
  } else {
    a1 = localToWorld(tA, -hsA, 0);
    a2 = localToWorld(tA, hsA, 0);
  }
  if (b.capsuleDirection === 0) {
    b1 = localToWorld(tB, 0, -hsB);
    b2 = localToWorld(tB, 0, hsB);
  } else {
    b1 = localToWorld(tB, -hsB, 0);
    b2 = localToWorld(tB, hsB, 0);
  }

  const closest = closestPointsOnSegments(a1, a2, b1, b2);
  const dx = closest.b.x - closest.a.x;
  const dy = closest.b.y - closest.a.y;
  const distSq = dx * dx + dy * dy;
  const sumR = a.radius + b.radius;

  if (distSq >= sumR * sumR) return null;

  const dist = Math.sqrt(distSq);
  const nx = dist > 1e-10 ? dx / dist : 0;
  const ny = dist > 1e-10 ? dy / dist : 1;
  const separation = dist - sumR;
  const contactX = closest.a.x + nx * a.radius;
  const contactY = closest.a.y + ny * a.radius;

  return createManifold(a, b, { x: nx, y: ny }, [
    createContact({ x: contactX, y: contactY }, { x: nx, y: ny }, separation),
  ]);
}

// ============================================================================
// CAPSULE vs POLYGON
// ============================================================================

function capsuleVsPolygon(
  capsule: ColliderState, poly: ColliderState,
  tCapsule: Transform2D, tPoly: Transform2D,
): CollisionManifold | null {
  // Decompose into circle vs polygon at closest segment point
  const hs = (capsule.capsuleHeight - capsule.radius * 2) * 0.5;
  let p1: PhysVec2, p2: PhysVec2;
  if (capsule.capsuleDirection === 0) {
    p1 = localToWorld(tCapsule, 0, -hs);
    p2 = localToWorld(tCapsule, 0, hs);
  } else {
    p1 = localToWorld(tCapsule, -hs, 0);
    p2 = localToWorld(tCapsule, hs, 0);
  }

  // Find closest point on capsule segment to polygon center
  const polyCenter = localToWorld(tPoly, 0, 0);
  const closest = closestPointOnSegment(polyCenter.x, polyCenter.y, p1, p2);

  const virtualCircle: ColliderState = {
    ...capsule,
    type: ColliderType.Circle,
    radius: capsule.radius,
    offset: { x: 0, y: 0 },
    rotation: 0,
  };
  const virtualTransform: Transform2D = { x: closest.x, y: closest.y, cos: 1, sin: 0 };

  const result = circleVsPolygon(virtualCircle, poly, virtualTransform, tPoly);
  if (result) {
    result.colliderA = capsule.colliderIndex;
    result.bodyIndexA = capsule.bodyIndex;
  }
  return result;
}

// ============================================================================
// POLYGON vs POLYGON (SAT with Sutherland-Hodgman clipping)
// ============================================================================

function polygonVsPolygon(
  a: ColliderState, b: ColliderState,
  tA: Transform2D, tB: Transform2D,
): CollisionManifold | null {
  return satConvexVsConvex(
    getPolygonVerticesWorld(a, tA),
    getPolygonNormalsWorld(a, tA),
    getPolygonVerticesWorld(b, tB),
    getPolygonNormalsWorld(b, tB),
    a, b,
  );
}

// ============================================================================
// SAT GENERIC CONVEX vs CONVEX
// ============================================================================

function satConvexVsConvex(
  vertsA: PhysVec2[], normalsA: PhysVec2[],
  vertsB: PhysVec2[], normalsB: PhysVec2[],
  colA: ColliderState, colB: ColliderState,
): CollisionManifold | null {
  let minPen = Infinity;
  let bestNormal: PhysVec2 = { x: 0, y: 1 };
  let flipped = false;

  // Test A normals
  for (const n of normalsA) {
    const sepA = projectMinMax(vertsA, n);
    const sepB = projectMinMax(vertsB, n);
    const overlap = Math.min(sepA.max - sepB.min, sepB.max - sepA.min);
    if (overlap <= 0) return null;
    if (overlap < minPen) {
      minPen = overlap;
      bestNormal = n;
      flipped = false;
    }
  }

  // Test B normals
  for (const n of normalsB) {
    const sepA = projectMinMax(vertsA, n);
    const sepB = projectMinMax(vertsB, n);
    const overlap = Math.min(sepA.max - sepB.min, sepB.max - sepA.min);
    if (overlap <= 0) return null;
    if (overlap < minPen) {
      minPen = overlap;
      bestNormal = n;
      flipped = true;
    }
  }

  // Ensure normal points from A to B
  const centerAx = centroidX(vertsA);
  const centerAy = centroidY(vertsA);
  const centerBx = centroidX(vertsB);
  const centerBy = centroidY(vertsB);
  const dx = centerBx - centerAx;
  const dy = centerBy - centerAy;
  const dot = dx * bestNormal.x + dy * bestNormal.y;

  let normal: PhysVec2;
  if (dot < 0) {
    normal = { x: -bestNormal.x, y: -bestNormal.y };
  } else {
    normal = { x: bestNormal.x, y: bestNormal.y };
  }

  // Find contact points via clipping
  const contacts = findContactPoints(vertsA, vertsB, normal, minPen);

  return createManifold(colA, colB, normal, contacts);
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function projectMinMax(verts: PhysVec2[], axis: PhysVec2): { min: number; max: number } {
  let min = Infinity, max = -Infinity;
  for (const v of verts) {
    const d = v.x * axis.x + v.y * axis.y;
    if (d < min) min = d;
    if (d > max) max = d;
  }
  return { min, max };
}

function centroidX(verts: PhysVec2[]): number {
  let sum = 0;
  for (const v of verts) sum += v.x;
  return sum / verts.length;
}

function centroidY(verts: PhysVec2[]): number {
  let sum = 0;
  for (const v of verts) sum += v.y;
  return sum / verts.length;
}

function findContactPoints(
  vertsA: PhysVec2[], vertsB: PhysVec2[],
  normal: PhysVec2, penetration: number,
): ContactPoint2D[] {
  // Find incident and reference edge
  const refEdge = findBestEdge(vertsA, { x: -normal.x, y: -normal.y });
  const incEdge = findBestEdge(vertsB, normal);

  // Clip incident against reference
  const clipped = clipSegment(
    incEdge.v1, incEdge.v2,
    refEdge.v1, refEdge.v2,
    { x: -normal.x, y: -normal.y },
  );

  const contacts: ContactPoint2D[] = [];
  for (let i = 0; i < Math.min(clipped.length, MAX_CONTACT_POINTS); i++) {
    const p = clipped[i];
    contacts.push(createContact(p, normal, -penetration));
  }

  if (contacts.length === 0) {
    // Fallback: use deepest point
    const deepest = findDeepestPoint(vertsB, normal);
    contacts.push(createContact(deepest, normal, -penetration));
  }

  return contacts;
}

function findBestEdge(verts: PhysVec2[], normal: PhysVec2): { v1: PhysVec2; v2: PhysVec2 } {
  let bestDot = -Infinity;
  let bestIdx = 0;

  for (let i = 0; i < verts.length; i++) {
    const dot = verts[i].x * normal.x + verts[i].y * normal.y;
    if (dot > bestDot) {
      bestDot = dot;
      bestIdx = i;
    }
  }

  const v = verts[bestIdx];
  const prev = verts[(bestIdx - 1 + verts.length) % verts.length];
  const next = verts[(bestIdx + 1) % verts.length];

  // Pick edge closest to normal
  const e1x = v.x - prev.x;
  const e1y = v.y - prev.y;
  const e2x = next.x - v.x;
  const e2y = next.y - v.y;

  const d1 = Math.abs(e1x * normal.x + e1y * normal.y);
  const d2 = Math.abs(e2x * normal.x + e2y * normal.y);

  return d1 < d2 ? { v1: prev, v2: v } : { v1: v, v2: next };
}

function findDeepestPoint(verts: PhysVec2[], normal: PhysVec2): PhysVec2 {
  let bestDot = Infinity;
  let best = verts[0];
  for (const v of verts) {
    const dot = v.x * normal.x + v.y * normal.y;
    if (dot < bestDot) {
      bestDot = dot;
      best = v;
    }
  }
  return best;
}

function clipSegment(
  v1: PhysVec2, v2: PhysVec2,
  e1: PhysVec2, e2: PhysVec2,
  normal: PhysVec2,
): PhysVec2[] {
  const points: PhysVec2[] = [v1, v2];
  // Clip against both side planes of the reference edge
  const edgeDir = { x: e2.x - e1.x, y: e2.y - e1.y };
  const len = Math.sqrt(edgeDir.x * edgeDir.x + edgeDir.y * edgeDir.y);
  if (len < 1e-10) return points;
  edgeDir.x /= len;
  edgeDir.y /= len;

  // Clip against e1 side
  const clipped1 = clipByPlane(points, { x: edgeDir.x, y: edgeDir.y },
    edgeDir.x * e1.x + edgeDir.y * e1.y);

  // Clip against e2 side
  const clipped2 = clipByPlane(clipped1, { x: -edgeDir.x, y: -edgeDir.y },
    -(edgeDir.x * e2.x + edgeDir.y * e2.y));

  return clipped2;
}

function clipByPlane(points: PhysVec2[], normal: PhysVec2, dist: number): PhysVec2[] {
  const result: PhysVec2[] = [];
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    const da = a.x * normal.x + a.y * normal.y - dist;
    const db = b.x * normal.x + b.y * normal.y - dist;

    if (da >= 0) result.push(a);
    if ((da >= 0) !== (db >= 0)) {
      const t = da / (da - db);
      result.push({
        x: a.x + t * (b.x - a.x),
        y: a.y + t * (b.y - a.y),
      });
    }
  }
  return result;
}

function closestPointOnSegment(px: number, py: number, a: PhysVec2, b: PhysVec2): PhysVec2 {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const apx = px - a.x;
  const apy = py - a.y;
  const t = Math.max(0, Math.min(1,
    (apx * abx + apy * aby) / (abx * abx + aby * aby || 1),
  ));
  return { x: a.x + t * abx, y: a.y + t * aby };
}

function closestPointsOnSegments(
  a1: PhysVec2, a2: PhysVec2,
  b1: PhysVec2, b2: PhysVec2,
): { a: PhysVec2; b: PhysVec2 } {
  const d1x = a2.x - a1.x, d1y = a2.y - a1.y;
  const d2x = b2.x - b1.x, d2y = b2.y - b1.y;
  const rx = a1.x - b1.x, ry = a1.y - b1.y;

  const a = d1x * d1x + d1y * d1y;
  const e = d2x * d2x + d2y * d2y;
  const f = d2x * rx + d2y * ry;

  let s: number, t: number;

  if (a <= 1e-10 && e <= 1e-10) {
    return { a: { ...a1 }, b: { ...b1 } };
  }

  if (a <= 1e-10) {
    s = 0;
    t = Math.max(0, Math.min(1, f / e));
  } else {
    const c = d1x * rx + d1y * ry;
    if (e <= 1e-10) {
      t = 0;
      s = Math.max(0, Math.min(1, -c / a));
    } else {
      const b2v = d1x * d2x + d1y * d2y;
      const denom = a * e - b2v * b2v;
      s = denom !== 0 ? Math.max(0, Math.min(1, (b2v * f - c * e) / denom)) : 0;
      t = Math.max(0, Math.min(1, (b2v * s + f) / e));
      // Recompute s
      s = Math.max(0, Math.min(1, (b2v * t - c) / a));
    }
  }

  return {
    a: { x: a1.x + s * d1x, y: a1.y + s * d1y },
    b: { x: b1.x + t * d2x, y: b1.y + t * d2y },
  };
}

// ============================================================================
// GEOMETRY HELPERS
// ============================================================================

function getBoxVerticesWorld(box: ColliderState, t: Transform2D): PhysVec2[] {
  const hx = box.halfExtents.x;
  const hy = box.halfExtents.y;
  return [
    localToWorld(t, -hx, -hy),
    localToWorld(t, hx, -hy),
    localToWorld(t, hx, hy),
    localToWorld(t, -hx, hy),
  ];
}

function getBoxNormalsWorld(t: Transform2D): PhysVec2[] {
  return [
    { x: t.cos, y: t.sin },   // right
    { x: -t.sin, y: t.cos },  // up
  ];
}

function getPolygonVerticesWorld(poly: ColliderState, t: Transform2D): PhysVec2[] {
  const result: PhysVec2[] = [];
  for (let i = 0; i < poly.vertexCount; i++) {
    const v = poly.vertices[i];
    result.push(localToWorld(t, v.x, v.y));
  }
  return result;
}

function getPolygonNormalsWorld(poly: ColliderState, t: Transform2D): PhysVec2[] {
  const result: PhysVec2[] = [];
  for (let i = 0; i < poly.vertexCount; i++) {
    const n = poly.normals[i];
    result.push({
      x: n.x * t.cos - n.y * t.sin,
      y: n.x * t.sin + n.y * t.cos,
    });
  }
  return result;
}

// ============================================================================
// MANIFOLD FACTORY
// ============================================================================

function createContact(point: PhysVec2, normal: PhysVec2, separation: number): ContactPoint2D {
  return {
    point: { x: point.x, y: point.y },
    normal: { x: normal.x, y: normal.y },
    separation,
    normalImpulse: 0,
    tangentImpulse: 0,
  };
}

/** Priority order for CombineMode: Maximum > Multiply > Minimum > Average */
const COMBINE_PRIORITY: Record<number, number> = {
  0: 0, // Average (lowest)
  1: 1, // Minimum
  3: 2, // Multiply
  2: 3, // Maximum (highest)
};

/** Combine two values based on CombineMode (default: multiply for friction, max for restitution) */
function combineMaterial(
  valA: number,
  valB: number,
  modeA: number | undefined,
  modeB: number | undefined,
  defaultMode: number,
): number {
  // Use the higher-priority mode from either material
  const priorityA = COMBINE_PRIORITY[modeA ?? defaultMode] ?? 0;
  const priorityB = COMBINE_PRIORITY[modeB ?? defaultMode] ?? 0;
  const mode = priorityA >= priorityB ? (modeA ?? defaultMode) : (modeB ?? defaultMode);
  
  switch (mode) {
    case 1: // Minimum
      return Math.min(valA, valB);
    case 2: // Maximum
      return Math.max(valA, valB);
    case 3: // Multiply
      return valA * valB;
    case 0: // Average
    default:
      return (valA + valB) * 0.5;
  }
}

function createManifold(
  a: ColliderState, b: ColliderState,
  normal: PhysVec2,
  contacts: ContactPoint2D[],
): CollisionManifold {
  // Combine friction: default to Multiply (3) for lower combined friction
  const friction = combineMaterial(
    a.material.friction,
    b.material.friction,
    a.material.frictionCombine,
    b.material.frictionCombine,
    3, // Multiply
  );
  // Combine restitution: default to Maximum (2)
  const restitution = combineMaterial(
    a.material.bounciness,
    b.material.bounciness,
    a.material.bouncinessCombine,
    b.material.bouncinessCombine,
    2, // Maximum
  );

  return {
    bodyIndexA: a.bodyIndex,
    bodyIndexB: b.bodyIndex,
    colliderA: a.colliderIndex,
    colliderB: b.colliderIndex,
    normal,
    contactCount: contacts.length,
    contacts,
    friction,
    restitution,
  };
}
